import React, { useContext, useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { AppContext } from '../App';
import {
  getMentorBySlug, MENTOR_REVIEWS, SESSION_TYPES,
  convertPrice, getAvatarUrl
} from '../data/mockData';
import { useSEO, jsonLd, getMentorOgImage, PROD_URL } from '../utils/seo';
import { FollowButton, FavoriteButton } from '../components/FollowButton';
import { useFollow } from '../context/FollowContext';
import './MentorProfile.css';
import ShareModal from '../components/ShareModal';
import { getMentorShareUrl, getReferralCode } from '../utils/referral';
import { useAuth } from '../context/AuthContext';
import { mentorService, reviewService } from '../services';


/* ─── Star display ─────────────────────────────────────── */
function Stars({ rating, size = '.9rem' }) {
  return (
    <span className="stars" style={{ fontSize: size }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ color: i <= Math.round(rating) ? '#f59e0b' : '#d1d5db' }}>★</span>
      ))}
    </span>
  );

}

/* ─── Rating bar ───────────────────────────────────────── */
function RatingBars({ reviews }) {
  const counts = [5,4,3,2,1].map(n => ({
    star: n,
    count: reviews.filter(r => r.rating === n).length,
  }));
  const max = Math.max(...counts.map(c => c.count), 1);
  return (
    <div className="rating-bars">
      {counts.map(c => (
        <div key={c.star} className="rb-row">
          <span className="rb-star">{c.star} ★</span>
          <div className="rb-track">
            <div className="rb-fill" style={{ width: `${(c.count / max) * 100}%` }} />
          </div>
          <span className="rb-count">{c.count}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Single Review Card ───────────────────────────────── */
function ReviewCard({ review: r }) {
  return (
    <div className="review-card">
      <div className="rc-header">
        <div className="rc-av-wrap">
          <img
            src={getAvatarUrl(r.avatarSeed, r.avatarBg, 44)}
            alt={r.name}
            className="rc-avatar"
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <div className="rc-avatar-fb" style={{ background: `#${r.avatarBg}` }}>{r.initials}</div>
        </div>
        <div className="rc-meta">
          <div className="rc-name">{r.name}</div>
          <div className="rc-session">{r.sessionType} · {r.date}</div>
        </div>
        <Stars rating={r.rating} size=".8rem" />
      </div>
      <p className="rc-comment">{r.comment}</p>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────── */
export default function MentorProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const { currency, setModalOpen } = useContext(AppContext);
  const { user } = useAuth();
  const [showShare, setShowShare] = useState(false);
  const { getFollowerCount } = useFollow();
  const [activeTab, setActiveTab] = useState('about');

  const isMock = process.env.REACT_APP_MOCK !== 'false';
  const [mentor,      setMentor]      = useState(() => {
    if (navState?.mentor) return navState.mentor;
    return isMock ? getMentorBySlug(slug) : null;
  });
  const [reviews,     setReviews]     = useState(() => {
    if (!isMock) return [];
    const m = getMentorBySlug(slug);
    return MENTOR_REVIEWS[m?.id] || [];
  });
  const [profileLoading, setProfileLoading] = useState(!isMock && !navState?.mentor);
  const [extraData, setExtraData] = useState(null);

  // Load rich profile data saved from dashboard
  useEffect(() => {
    try {
      const key = `db_mentor_profile_${slug}`;
      const raw = localStorage.getItem(key);
      if (raw) setExtraData(JSON.parse(raw));
    } catch (_) {}
  }, [slug]);

  useEffect(() => {
    if (isMock) return;
    setProfileLoading(true);
    mentorService.getMentorBySlug(slug)
      .then(res => {
        if (res?.mentor) {
          setMentor(res.mentor);
          const mentorId = res.mentor._id || res.mentor.id;
          if (mentorId) {
            reviewService.getMentorReviews(mentorId)
              .then(r => { if (r?.reviews?.length) setReviews(r.reviews); })
              .catch(() => {});
          }
          if (res.mentor.reviews?.length) setReviews(res.mentor.reviews.map(r => ({
            ...r,
            id:          r._id || r.id,
            rating:      r.rating  ?? r.stars  ?? 0,
            comment:     r.comment || r.testimonial || '',
            name:        r.name    || r.reviewer   || 'Anonymous',
            avatarSeed:  r.avatarSeed  || r.name || r.reviewer || 'user',
            avatarBg:    r.avatarBg   || '6366f1',
            initials:    r.initials   || (r.name || r.reviewer || 'A')[0].toUpperCase(),
            sessionType: r.sessionType || 'Session',
          })));
        }
      })
      .catch(() => {})
      .finally(() => setProfileLoading(false));
  }, [slug]); // eslint-disable-line

  /* ─────────────────────────────────────────────────────────
   * LinkedIn-style profile SEO
   *
   * Title:    "Amar Krishnan - Senior Software Engineer at Ex-Google | DevBrains"
   * Desc:     "Amar Krishnan is a verified Senior Software Engineer at Ex-Google
   *            with 8 years of experience. Skills: React, Node.js, AWS. ⭐ 4.97
   *            · 412 sessions completed. Book a 1:1 session on DevBrains."
   * OG image: Mentor avatar (DiceBear, deterministic, stable URL — swap for
   *            real photo when upload system exists)
   * og:type:  "profile" + profile:first_name / profile:last_name
   * JSON-LD:  @graph with Person + BreadcrumbList (exactly what Google uses
   *            when parsing LinkedIn profiles)
   * ─────────────────────────────────────────────────────────
   */
  const mentorOgImage  = mentor ? getMentorOgImage(mentor) : null;
  const profileUrl     = mentor ? `${PROD_URL}/mentors/${mentor.slug}` : null;
  const nameParts      = mentor?.name ? mentor.name.split(' ') : [];
  const avgRatingVal   = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : mentor?.rating;

  const seoTitle = mentor
    ? `${mentor.name} - ${mentor.role} at ${mentor.company}`
    : 'Mentor Not Found';

  const seoDesc = mentor
    ? `${mentor.name} is a verified ${mentor.role} at ${mentor.company} with ` +
      `${mentor.experience} years of experience. ` +
      `Skills: ${(mentor.skills || []).slice(0, 4).join(', ')}. ` +
      `⭐ ${avgRatingVal} · ${mentor.sessionsCompleted} sessions completed. ` +
      `Book a 1:1 mentorship session on DevBrains.`
    : 'Mentor not found on DevBrains.';

  useSEO(mentor ? {
    title:       seoTitle,
    description: seoDesc,
    image:       mentorOgImage,
    type:        'profile',
    canonical:   profileUrl,
    profile: {
      firstName: nameParts[0] || '',
      lastName:  nameParts.slice(1).join(' ') || '',
      username:  mentor.slug,
    },
    jsonLd: jsonLd.graph(
      jsonLd.person(mentor),           // Full Schema.org/Person
      jsonLd.profileBreadcrumb(mentor) // BreadcrumbList for Google
    ),
  } : {
    title:   'Mentor Not Found',
    noIndex: true,
  });

  if (profileLoading) {
    return null;
  }

  if (!mentor) {
    return (
      <div className="profile-not-found">
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔍</div>
        <h2>Mentor Not Found</h2>
        <p>The mentor profile you're looking for doesn't exist or has been removed.</p>
        <Link to="/mentors" className="btn btn-primary" style={{ marginTop: 20 }}>
          Browse All Mentors
        </Link>
      </div>
    );
  }

  // avgRatingVal computed above in SEO block — alias for template use
  const avgRating = avgRatingVal;

  const workExp     = extraData?.workExperience?.filter(j => j.company || j.role) || [];
  const education   = extraData?.education?.filter(e => e.institution || e.degree) || [];
  const certs       = (extraData?.certifications || []).filter(Boolean);
  const twitterUrl  = extraData?.twitter || mentor?.twitter;
  const portfolioUrl = extraData?.portfolio || mentor?.portfolio;

  const price = convertPrice(mentor.priceINR, currency);

  const tabs = ['about', 'reviews', 'sessions'];

  return (
    <>
    <div className="profile-page">

      {/* ── Breadcrumb ── */}
      <div className="profile-breadcrumb">
        <div className="breadcrumb-inner">
          <Link to="/mentors">← Browse Mentors</Link>
          <span className="bc-sep">/</span>
          <span>{mentor.name}</span>
        </div>
      </div>

      {/* ── Hero Banner ── */}
      <div className="profile-hero">
        <div className="profile-hero-inner">

          {/* Left: avatar + info */}
          <div className="ph-left">
            <div className="ph-avatar-wrap">
              <img
                src={getAvatarUrl(mentor.avatarSeed, mentor.avatarBg, 120)}
                alt={mentor.name}
                className="ph-avatar"
                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
              <div className="ph-avatar-fb" style={{ background: `#${mentor.avatarBg}` }}>
                {mentor.initials}
              </div>
              {mentor.isAvailableToday && (
                <div className="ph-online-badge">● Available today</div>
              )}
            </div>

            <div className="ph-info">
              <div className="ph-top-row">
                <h1 className="ph-name">{mentor.name}</h1>
                {mentor.badge && (
                  <span className={`badge badge-${mentor.badgeType}`}>{mentor.badge}</span>
                )}
              </div>
              <div className="ph-role">{mentor.role} · {mentor.company}</div>
              <div className="ph-location">{mentor.location}</div>

              <div className="ph-stats">
                <div className="ph-stat">
                  <Stars rating={avgRating} size=".85rem" />
                  <span className="ph-stat-val">{avgRating}</span>
                  <span className="ph-stat-lbl">({reviews.length} reviews)</span>
                </div>
                <span className="ph-stat-sep">·</span>
                <div className="ph-stat">
                  <span className="ph-stat-val">{mentor.sessionsCompleted}</span>
                  <span className="ph-stat-lbl">sessions</span>
                </div>
                <span className="ph-stat-sep">·</span>
                <div className="ph-stat">
                  <span className="ph-stat-val">{mentor.experience} yrs</span>
                  <span className="ph-stat-lbl">experience</span>
                </div>
                <span className="ph-stat-sep">·</span>
                <div className="ph-stat">
                  <span className="ph-stat-val">{(getFollowerCount(mentor.id) >= 1000 ? (getFollowerCount(mentor.id)/1000).toFixed(1)+'k' : getFollowerCount(mentor.id))}</span>
                  <span className="ph-stat-lbl">followers</span>
                </div>
              </div>

              <div className="ph-skills">
                {mentor.skills.map(s => (
                  <span key={s} className="badge badge-gray">{s}</span>
                ))}
              </div>

              {/* Follow & Favorite actions */}
              <div className="ph-follow-row">
                <FollowButton
                  mentorId={mentor.id}
                  size="md"
                  showCount={true}
                  onLoginRequired={() => setModalOpen(true)}
                />
                <FavoriteButton
                  mentorId={mentor.id}
                  size="md"
                  onLoginRequired={() => setModalOpen(true)}
                />
                <button className="btn btn-ghost btn-sm" style={{fontSize:'.8rem'}} onClick={() => {
                  if(navigator.share) navigator.share({title: mentor.name, url: window.location.href});
                  else navigator.clipboard?.writeText(window.location.href);
                }}>🔗 Share</button>
              </div>

              <div className="ph-meta-row">
                <span className="ph-meta-item">🌐 {mentor.languages.join(', ')}</span>
                <span className="ph-meta-item">🕐 {mentor.timezone}</span>
                <span className="ph-meta-item">⚡ {mentor.responseTime}</span>
              </div>
            </div>
          </div>

          {/* Right: booking card */}
          <div className="ph-book-card">
            <div className="ph-book-price">
              {price}<span>/hr</span>
            </div>
            <div className="ph-book-avail" style={{ color: mentor.availabilityColor }}>
              {mentor.availabilityLabel}
            </div>

            <div className="ph-session-types">
              {mentor.sessionTypes.map((st, i) => {
                const stName = st?.name || st;
                const info = SESSION_TYPES[stName];
                return (
                  <div key={stName || i} className="ph-session-row">
                    <span>{info?.icon} {stName}</span>
                    <span className="ph-session-dur">{st?.duration ? `${st.duration} min` : info?.duration}</span>
                  </div>
                );
              })}
            </div>

            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}
              onClick={() => navigate(`/mentors/${mentor.slug}/book`)}
            >
              📅 Book a Session
            </button>
            <button
              className="btn btn-outline"
              style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
              onClick={() => navigate(`/mentors/${mentor.slug}/book`)}
            >
              💬 Message Mentor
            </button>

            <div className="ph-book-trust">
              <span>🔒 Escrow protected</span>
              <span>🔄 Free reschedule</span>
              <span>💯 Satisfaction guarantee</span>
            </div>
          </div>

        </div>
      </div>

      {/* ── Mobile Sticky Book Bar (visible below 900px) ── */}
      <div className="ph-mobile-book-bar">
        <div className="ph-mbb-info">
          <span className="ph-mbb-price">{price}<span>/hr</span></span>
          <span className="ph-mbb-avail" style={{ color: mentor.availabilityColor }}>{mentor.availabilityLabel}</span>
        </div>
        <button
          className="btn btn-primary"
          style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          onClick={() => navigate(`/mentors/${mentor.slug}/book`)}
        >
          📅 Book Session
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="profile-tabs-bar">
        <div className="profile-tabs-inner">
          {tabs.map(t => (
            <button
              key={t}
              className={`profile-tab ${activeTab === t ? 'active' : ''}`}
              onClick={() => setActiveTab(t)}
            >
              {t === 'about' && '👤 About'}
              {t === 'reviews' && `⭐ Reviews (${reviews.length})`}
              {t === 'sessions' && '📅 Book a Session'}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div className="profile-content">
        <div className="profile-content-inner">

          {/* ── ABOUT TAB ── */}
          {activeTab === 'about' && (
            <div className="about-tab">
              <div className="about-main">

                <section className="about-section">
                  <h2 className="section-heading">About {mentor.name}</h2>
                  <p className="about-bio">{mentor.bio}</p>
                  <p className="about-bio" style={{ marginTop: 14 }}>
                    With {mentor.experience}+ years at {mentor.company}, I bring real-world experience to every session.
                    I specialise in {mentor.specialization} and have helped {mentor.sessionsCompleted}+ learners
                    achieve their career goals, from landing their first tech job to moving into senior engineering roles.
                  </p>
                </section>

                <section className="about-section">
                  <h2 className="section-heading">What You'll Get</h2>
                  <div className="what-grid">
                    {[
                      { icon: '🎯', title: 'Personalized guidance', desc: 'Every session is tailored to your specific goals and current level.' },
                      { icon: '💡', title: 'Real-world insight', desc: `Learn from someone who has worked at ${mentor.company} and built production systems.` },
                      { icon: '📋', title: 'Session notes', desc: 'You\'ll get a written summary of key takeaways after every session.' },
                      { icon: '🔗', title: 'Resource links', desc: 'Curated articles, docs, and tools relevant to what we covered.' },
                    ].map(item => (
                      <div key={item.title} className="what-item">
                        <span className="what-ico">{item.icon}</span>
                        <div>
                          <div className="what-title">{item.title}</div>
                          <div className="what-desc">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="about-section">
                  <h2 className="section-heading">Skills & Expertise</h2>
                  <div className="skills-grid">
                    {mentor.skills.map(s => (
                      <div key={s} className="skill-pill">
                        <span className="skill-check">✓</span> {s}
                      </div>
                    ))}
                  </div>
                </section>

                {workExp.length > 0 && (
                  <section className="about-section">
                    <h2 className="section-heading">Work Experience</h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {workExp.map((job, i) => (
                        <div key={job.id || i} style={{ display: 'flex', gap: 16, paddingBottom: 20, position: 'relative' }}>
                          {i < workExp.length - 1 && (
                            <div style={{ position: 'absolute', left: 19, top: 40, bottom: 0, width: 2, background: '#e5e7eb' }} />
                          )}
                          <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#f0fdf4', border: '2px solid #16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1, fontSize: '1rem' }}>
                            🏢
                          </div>
                          <div style={{ flex: 1, paddingTop: 6 }}>
                            <div style={{ fontWeight: 800, fontSize: '.95rem', color: '#1a1a2e' }}>{job.role}</div>
                            <div style={{ fontSize: '.84rem', color: '#16a34a', fontWeight: 700, marginTop: 2 }}>{job.company}</div>
                            <div style={{ fontSize: '.75rem', color: '#9ca3af', marginTop: 2 }}>
                              {job.startYear}{job.startYear && (job.endYear || job.isCurrent) ? ' – ' : ''}{job.isCurrent ? 'Present' : job.endYear}
                            </div>
                            {job.description && <p style={{ fontSize: '.84rem', color: '#6b7280', marginTop: 6, lineHeight: 1.65 }}>{job.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {education.length > 0 && (
                  <section className="about-section">
                    <h2 className="section-heading">Education</h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {education.map((edu, i) => (
                        <div key={edu.id || i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#eff6ff', border: '2px solid #2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.1rem' }}>
                            🎓
                          </div>
                          <div>
                            <div style={{ fontWeight: 800, fontSize: '.9rem', color: '#1a1a2e' }}>{edu.degree}</div>
                            <div style={{ fontSize: '.84rem', color: '#2563eb', fontWeight: 700, marginTop: 1 }}>{edu.institution}</div>
                            {edu.year && <div style={{ fontSize: '.75rem', color: '#9ca3af', marginTop: 2 }}>{edu.year}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {certs.length > 0 && (
                  <section className="about-section">
                    <h2 className="section-heading">Certifications & Achievements</h2>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {certs.filter(Boolean).map((c, i) => (
                        <span key={i} style={{ background: '#fef9c3', border: '1px solid #fde68a', color: '#92400e', fontSize: '.78rem', fontWeight: 700, padding: '5px 14px', borderRadius: 8 }}>
                          🏆 {c}
                        </span>
                      ))}
                    </div>
                  </section>
                )}

                <section className="about-section">
                  <h2 className="section-heading">Quick Facts</h2>
                  <div className="facts-grid">
                    <div className="fact-row"><span className="fact-label">🏢 Company</span><span className="fact-val">{mentor.company}</span></div>
                    <div className="fact-row"><span className="fact-label">⏱ Experience</span><span className="fact-val">{mentor.experience} years</span></div>
                    <div className="fact-row"><span className="fact-label">📍 Location</span><span className="fact-val">{mentor.location}</span></div>
                    <div className="fact-row"><span className="fact-label">🕐 Timezone</span><span className="fact-val">{mentor.timezone}</span></div>
                    <div className="fact-row"><span className="fact-label">🌐 Languages</span><span className="fact-val">{mentor.languages.join(', ')}</span></div>
                    <div className="fact-row"><span className="fact-label">⚡ Response</span><span className="fact-val">{mentor.responseTime}</span></div>
                    <div className="fact-row"><span className="fact-label">✅ Sessions</span><span className="fact-val">{mentor.sessionsCompleted} completed</span></div>
                    <div className="fact-row"><span className="fact-label">⭐ Rating</span><span className="fact-val">{avgRating} / 5.0</span></div>
                  </div>
                </section>

                {/* Recent reviews preview */}
                {reviews.length > 0 && (
                  <section className="about-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                      <h2 className="section-heading" style={{ marginBottom: 0 }}>Recent Reviews</h2>
                      <button className="btn-text-link" onClick={() => setActiveTab('reviews')}>
                        See all {reviews.length} reviews →
                      </button>
                    </div>
                    <div className="reviews-list">
                      {reviews.slice(0, 2).map(r => <ReviewCard key={r.id} review={r} />)}
                    </div>
                  </section>
                )}
              </div>

              {/* Sidebar */}
              <aside className="about-sidebar">
                <div className="sidebar-book-card">
                  <div className="sbc-price">{price}<span>/hr</span></div>
                  <div className="sbc-avail" style={{ color: mentor.availabilityColor }}>{mentor.availabilityLabel}</div>
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center', marginTop: 14, padding: '12px' }}
                    onClick={() => navigate(`/mentors/${mentor.slug}/book`)}
                  >
                    📅 Book a Session
                  </button>
                  <div className="sbc-sessions">
                    {mentor.sessionTypes.map((st, i) => {
                      const stName = st?.name || st;
                      const info = SESSION_TYPES[stName];
                      return (
                        <div key={stName || i} className="sbc-session-row">
                          <span>{info?.icon} {stName}</span>
                          <span className="sbc-dur">{st?.duration ? `${st.duration} min` : info?.duration}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="sidebar-trust-card">
                  <h4>Why DevBrains?</h4>
                  <div className="stc-item"><span>✅</span><span>Verified mentor identity</span></div>
                  <div className="stc-item"><span>🔒</span><span>Razorpay escrow — pay only after session</span></div>
                  <div className="stc-item"><span>🔄</span><span>Free rescheduling up to 24hrs before</span></div>
                  <div className="stc-item"><span>💯</span><span>Satisfaction guarantee or money back</span></div>
                  {(twitterUrl || portfolioUrl || (extraData?.linkedinUrl || mentor?.linkedinUrl)) && (
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {(extraData?.linkedinUrl || mentor?.linkedinUrl) && (
                        <a
                          href={(extraData?.linkedinUrl || mentor?.linkedinUrl)}
                          target="_blank"
                          rel="noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f0f7ff', color: '#0a66c2', fontSize: '.75rem', fontWeight: 700, padding: '4px 10px', borderRadius: 6, textDecoration: 'none', border: '1px solid #dbeafe' }}
                        >
                          in LinkedIn
                        </a>
                      )}
                      {twitterUrl && (
                        <a
                          href={twitterUrl.startsWith('http') ? twitterUrl : `https://${twitterUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f0f9ff', color: '#0284c7', fontSize: '.75rem', fontWeight: 700, padding: '4px 10px', borderRadius: 6, textDecoration: 'none', border: '1px solid #e0f2fe' }}
                        >
                          𝕏 Twitter
                        </a>
                      )}
                      {portfolioUrl && (
                        <a
                          href={portfolioUrl.startsWith('http') ? portfolioUrl : `https://${portfolioUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#faf5ff', color: '#7c3aed', fontSize: '.75rem', fontWeight: 700, padding: '4px 10px', borderRadius: 6, textDecoration: 'none', border: '1px solid #ede9fe' }}
                        >
                          🌐 Portfolio
                        </a>
                      )}
                    </div>
                  )}
                </div>

                <div className="sidebar-stats-card">
                  <div className="stat-row"><span className="stat-big">{avgRating}</span><Stars rating={avgRating} /></div>
                  <div className="stat-sub">{reviews.length} verified reviews</div>
                  <div className="stat-divider" />
                  <div className="stat-row2"><span>{mentor.sessionsCompleted}</span><span className="stat-lbl2">sessions done</span></div>
                </div>
              </aside>
            </div>
          )}

          {/* ── REVIEWS TAB ── */}
          {activeTab === 'reviews' && (
            <div className="reviews-tab">
              <div className="reviews-main">
                <div className="reviews-overview">
                  <div className="ro-big-score">
                    <div className="ro-num">{avgRating}</div>
                    <Stars rating={avgRating} size="1.2rem" />
                    <div className="ro-total">{reviews.length} reviews</div>
                  </div>
                  <RatingBars reviews={reviews} />
                </div>

                <div className="reviews-filter-row">
                  <span className="reviews-count-label">All Reviews ({reviews.length})</span>
                  <div className="review-filter-chips">
                    {['All', ...mentor.sessionTypes.map(st => st?.name || st)].map(f => (
                      <button key={f} className={`chip ${f === 'All' ? 'active' : ''}`}>{f}</button>
                    ))}
                  </div>
                </div>

                <div className="reviews-list">
                  {reviews.map(r => <ReviewCard key={r.id} review={r} />)}
                </div>
              </div>

              <aside className="reviews-sidebar">
                <div className="sidebar-book-card">
                  <div className="sbc-price">{price}<span>/hr</span></div>
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center', marginTop: 14, padding: '12px' }}
                    onClick={() => navigate(`/mentors/${mentor.slug}/book`)}
                  >
                    📅 Book a Session
                  </button>
                </div>
              </aside>
            </div>
          )}

          {/* ── SESSIONS TAB ── */}
          {activeTab === 'sessions' && (
            <div className="sessions-tab">
              <h2 className="section-heading">Choose Your Session Type</h2>
              <p style={{ color: 'var(--ink3)', marginBottom: 28 }}>
                Select the type of session you need, then pick a date and time that works for you.
                No login required until you're ready to confirm.
              </p>
              <div className="session-types-grid">
                {mentor.sessionTypes.map((st, i) => {
                  const stName = st?.name || st;
                  const info = SESSION_TYPES[stName];
                  const price = st?.priceINR || Math.round((mentor.priceINR || 0) * (info?.multiplier || 1));
                  const sessionPrice = convertPrice(price, currency);
                  return (
                    <div key={stName || i} className="session-type-card">
                      <div className="stc-ico">{info?.icon || '💬'}</div>
                      <div className="stc-body">
                        <div className="stc-name">{stName}</div>
                        <div className="stc-desc">{info?.desc || ''}</div>
                        <div className="stc-meta">
                          <span className="stc-dur">⏱ {st?.duration ? `${st.duration} min` : (info?.duration || '60 min')}</span>
                          <span className="stc-price">{sessionPrice}</span>
                        </div>
                      </div>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate(`/mentors/${mentor.slug}/book`, { state: { sessionType: st } })}
                      >
                        Select →
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
    {showShare && (
      <ShareModal
        mentor={mentor}
        shareUrl={getMentorShareUrl(mentor.slug, user ? getReferralCode(user.id) : '')}
        referralCode={user ? getReferralCode(user.id) : ''}
        onClose={() => setShowShare(false)}
        mode="mentor"
      />
    )}
    </>
  );
}
